
import React from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  LineChart, Line, PieChart, Pie, Cell, Legend 
} from 'recharts';

interface ChartData {
  name: string;
  value: number;
  [key: string]: any;
}

interface ReportChartsProps {
  type: 'bar' | 'line' | 'pie';
  data: ChartData[];
  title: string;
  isLoading?: boolean;
  unit?: string;
  isDark?: boolean;
}

const COLORS = ['#FFDB00', '#3b82f6', '#10b981', '#f43f5e', '#8b5cf6', '#f59e0b', '#06b6d4'];

export const ReportCharts: React.FC<ReportChartsProps> = ({
  type,
  data,
  title,
  isLoading,
  unit = 'Activities',
  isDark = false
}) => {
  if (isLoading) {
    return (
      <div className={`p-6 h-[400px] flex flex-col gap-4 ${isDark ? 'bg-slate-900/50' : 'card-standard'}`}>
        <div className={`h-4 w-32 animate-pulse rounded ${isDark ? 'bg-white/10' : 'bg-slate-100'}`} />
        <div className={`flex-1 animate-pulse rounded-lg ${isDark ? 'bg-white/5' : 'bg-slate-50'}`} />
      </div>
    );
  }

  const tooltipContentStyle = { 
    backgroundColor: isDark ? '#0f172a' : 'white', 
    border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, 
    borderRadius: '0.375rem',
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    fontSize: '12px',
    fontWeight: '700',
    color: isDark ? 'white' : '#0f172a'
  };

  const axisTickStyle = { 
    fill: isDark ? 'rgba(255,255,255,0.4)' : '#64748b', 
    fontSize: 10, 
    fontWeight: 700 
  };

  const renderChart = () => {
    const themeColors = isDark ? COLORS : COLORS; // Keep consistent for now but could vary

    if (!data || data.length === 0) {
      return (
        <div className="h-full w-full flex flex-col items-center justify-center rounded-xl bg-white/2 border border-white/5 p-4">
          <p className="text-[9px] font-black uppercase text-white/10 tracking-[0.3em]">Analysis Pending</p>
        </div>
      );
    }

    switch (type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'} />
              <XAxis 
                dataKey="name" 
                axisLine={false} 
                tickLine={false} 
                tick={axisTickStyle}
                dy={10}
              />
              <YAxis 
                axisLine={false} 
                tickLine={false} 
                tick={axisTickStyle}
              />
              <Tooltip 
                cursor={{ fill: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc' }}
                contentStyle={tooltipContentStyle}
                itemStyle={{ color: isDark ? 'white' : '#0f172a' }}
                labelStyle={{ color: isDark ? 'white' : '#0f172a' }}
                formatter={(value: number) => [`${value} ${unit}`, 'Total']}
              />
              <Bar dataKey="value" fill="#FFDB00" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );
      case 'line':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'} />
              <XAxis 
                dataKey="name" 
                axisLine={false} 
                tickLine={false} 
                tick={axisTickStyle}
                dy={10}
              />
              <YAxis 
                axisLine={false} 
                tickLine={false} 
                tick={axisTickStyle}
              />
              <Tooltip 
                contentStyle={tooltipContentStyle}
                itemStyle={{ color: isDark ? 'white' : '#0f172a' }}
                labelStyle={{ color: isDark ? 'white' : '#0f172a' }}
                formatter={(value: number) => [`${value} ${unit}`, 'Total']}
              />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="#FFDB00" 
                strokeWidth={3} 
                dot={{ fill: '#FFDB00', strokeWidth: 2, r: 4, stroke: isDark ? '#0f172a' : 'white' }}
                activeDot={{ r: 6, strokeWidth: 0 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <Pie
                data={data}
                cx="50%"
                cy="45%"
                innerRadius="40%"
                outerRadius="60%"
                paddingAngle={4}
                dataKey="value"
              >
                {data.map((_, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS[index % COLORS.length]} 
                    stroke={isDark ? '#0f172a' : 'white'} 
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={tooltipContentStyle}
                itemStyle={{ color: isDark ? 'white' : '#0f172a' }}
                labelStyle={{ color: isDark ? 'white' : '#0f172a' }}
                formatter={(value: number) => [`${value} ${unit}`, 'Total']}
              />
              <Legend 
                verticalAlign="bottom" 
                align="center"
                iconType="circle"
                iconSize={6}
                layout="horizontal"
                wrapperStyle={{ 
                  paddingTop: '10px',
                  fontSize: '9px'
                }}
                formatter={(value) => (
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-white/60' : 'text-slate-500'}`}>{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`flex flex-col h-full w-full ${isLoading ? 'animate-pulse' : ''}`}>
      {title && <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-6">{title}</h3>}
      <div className="flex-1 min-h-0 relative">
        {renderChart()}
      </div>
    </div>
  );
};
